import { Type, type Static } from "@sinclair/typebox";
import { UserAutomationResultSchema } from "./user-automation.ts";
import { UuidSchema } from "./identifiers.ts";
import { RetrievalSourceSpanSchema } from "./knowledge-retrieval.ts";
import { KnowledgeExpertActivitySummarySchema } from "./knowledge-expert-activity.ts";
import { KNOWLEDGE_EXPERT_AGENT_KEY } from "./knowledge-expert.ts";
import { PptExpertActivitySummarySchema } from "./ppt-expert-activity.ts";
import { PPT_EXPERT_AGENT_KEY } from "./ppt-expert.ts";
import { PresentationCheckpointHintSchema } from "./ppt-expert.ts";
import { ExcelExpertActivitySummarySchema } from "./excel-expert-activity.ts";
import { EXCEL_EXPERT_AGENT_KEY } from "./excel-expert.ts";
import {
  BROWSER_EXPERT_AGENT_KEY,
  BrowserExpertActivitySummarySchema
} from "./browser-expert.ts";

export const AGENT_MESSAGE_KEY = "io.matrix_idp.agent.message" as const;
export const AGENT_APPROVAL_RESPONSE_KEY = "io.matrix_idp.agent.approval_response" as const;

const MatrixEventIdSchema = Type.String({
  minLength: 4,
  maxLength: 512,
  pattern: "^\\$[^\\s]+$"
});

export const AgentMessageCitationSchema = Type.Object(
  {
    documentId: UuidSchema,
    title: Type.String({ minLength: 1, maxLength: 1_024 }),
    sourceSpans: Type.Array(RetrievalSourceSpanSchema, { maxItems: 128 })
  },
  { additionalProperties: false }
);

export const AgentInvocationTokenUsageSchema = Type.Object(
  {
    inputTokens: Type.Integer({ minimum: 0 }),
    outputTokens: Type.Integer({ minimum: 0 }),
    thinkingTokens: Type.Optional(Type.Integer({ minimum: 0 })),
    totalTokens: Type.Integer({ minimum: 0 })
  },
  { additionalProperties: false }
);

export const AgentInvocationMetricsSchema = Type.Object(
  {
    durationMs: Type.Integer({ minimum: 0 }),
    tokenUsage: AgentInvocationTokenUsageSchema,
    outputTokensPerSecond: Type.Optional(Type.Integer({ minimum: 1, maximum: 1_000_000 }))
  },
  { additionalProperties: false }
);

const knowledgeBase = {
  automation: Type.Optional(UserAutomationResultSchema),
  version: Type.Literal("1.0"),
  agentKey: Type.Literal(KNOWLEDGE_EXPERT_AGENT_KEY),
  sourceEventId: MatrixEventIdSchema,
  activitySummary: Type.Optional(KnowledgeExpertActivitySummarySchema),
  invocationMetrics: Type.Optional(AgentInvocationMetricsSchema)
};

const pptBase = {
  automation: Type.Optional(UserAutomationResultSchema),
  version: Type.Literal("1.0"),
  agentKey: Type.Literal(PPT_EXPERT_AGENT_KEY),
  sourceEventId: MatrixEventIdSchema,
  activitySummary: Type.Optional(PptExpertActivitySummarySchema),
  invocationMetrics: Type.Optional(AgentInvocationMetricsSchema),
  presentation: Type.Optional(
    Type.Object(
      { checkpoint: Type.Optional(PresentationCheckpointHintSchema) },
      { additionalProperties: false }
    )
  )
};

const excelBase = {
  automation: Type.Optional(UserAutomationResultSchema),
  version: Type.Literal("1.0"),
  agentKey: Type.Literal(EXCEL_EXPERT_AGENT_KEY),
  sourceEventId: MatrixEventIdSchema,
  activitySummary: Type.Optional(ExcelExpertActivitySummarySchema),
  invocationMetrics: Type.Optional(AgentInvocationMetricsSchema)
};

const browserBase = {
  automation: Type.Optional(UserAutomationResultSchema),
  version: Type.Literal("1.0"),
  agentKey: Type.Literal(BROWSER_EXPERT_AGENT_KEY),
  sourceEventId: MatrixEventIdSchema,
  mode: Type.Union([
    Type.Literal("program_authoring"),
    Type.Literal("conversation_execution")
  ]),
  activitySummary: Type.Optional(BrowserExpertActivitySummarySchema),
  invocationMetrics: Type.Optional(AgentInvocationMetricsSchema)
};

export const AgentAnswerMessageSchema = Type.Object(
  {
    ...knowledgeBase,
    kind: Type.Literal("answer"),
    answer: Type.Object(
      {
        outcome: Type.Union([Type.Literal("answered"), Type.Literal("abstained")]),
        citations: Type.Array(AgentMessageCitationSchema, { maxItems: 32 })
      },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);

const KnowledgeAuthorizationSchema = Type.Union([
  Type.Object(
    {
      requiredAction: Type.Literal("knowledge.query"),
      reason: Type.Literal("product_permission"),
      resolution: Type.Literal("contact_administrator")
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      requiredAction: Type.Literal("knowledge.query"),
      reason: Type.Literal("resource_permission"),
      resolution: Type.Literal("contact_administrator")
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      requiredAction: Type.Literal("knowledge.query"),
      reason: Type.Literal("room_membership"),
      resolution: Type.Literal("recheck")
    },
    { additionalProperties: false }
  )
]);

export const AgentAuthorizationRequiredMessageSchema = Type.Object(
  {
    ...knowledgeBase,
    kind: Type.Literal("authorization_required"),
    authorization: KnowledgeAuthorizationSchema
  },
  { additionalProperties: false }
);

const FailureSchema = Type.Object(
  {
    reason: Type.Union([
      Type.Literal("busy"),
      Type.Literal("temporarily_unavailable"),
      Type.Literal("cannot_complete")
    ]),
    retryable: Type.Boolean()
  },
  { additionalProperties: false }
);

export const AgentFailureMessageSchema = Type.Object(
  { ...knowledgeBase, kind: Type.Literal("failure"), failure: FailureSchema },
  { additionalProperties: false }
);

const CancellationSchema = Type.Object(
  {
    reason: Type.Literal("human_request"),
    requestEventId: MatrixEventIdSchema
  },
  { additionalProperties: false }
);

export const AgentCancelledMessageSchema = Type.Object(
  { ...knowledgeBase, kind: Type.Literal("cancelled"), cancellation: CancellationSchema },
  { additionalProperties: false }
);

export const AgentApprovalMessageSchema = Type.Object(
  {
    ...knowledgeBase,
    kind: Type.Literal("approval_required"),
    approval: Type.Object(
      {
        approvalId: UuidSchema,
        action: Type.String({ minLength: 1, maxLength: 128 }),
        title: Type.String({ minLength: 1, maxLength: 256 }),
        detail: Type.String({ minLength: 1, maxLength: 2_048 })
      },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);

export const PptAgentAnswerMessageSchema = Type.Object(
  {
    ...pptBase,
    kind: Type.Literal("answer"),
    answer: Type.Object(
      { outcome: Type.Union([Type.Literal("answered"), Type.Literal("abstained")]) },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);

export const PptAgentAuthorizationRequiredMessageSchema = Type.Object(
  {
    ...pptBase,
    kind: Type.Literal("authorization_required"),
    authorization: Type.Union([
      Type.Object(
        {
          requiredAction: Type.Union([
            Type.Literal("knowledge.query"),
            Type.Literal("agent.files")
          ]),
          reason: Type.Union([
            Type.Literal("product_permission"),
            Type.Literal("resource_permission")
          ]),
          resolution: Type.Literal("contact_administrator")
        },
        { additionalProperties: false }
      ),
      Type.Object(
        {
          requiredAction: Type.Union([
            Type.Literal("knowledge.query"),
            Type.Literal("agent.files")
          ]),
          reason: Type.Literal("room_membership"),
          resolution: Type.Literal("recheck")
        },
        { additionalProperties: false }
      )
    ])
  },
  { additionalProperties: false }
);

export const PptAgentFailureMessageSchema = Type.Object(
  { ...pptBase, kind: Type.Literal("failure"), failure: FailureSchema },
  { additionalProperties: false }
);

export const PptAgentCancelledMessageSchema = Type.Object(
  { ...pptBase, kind: Type.Literal("cancelled"), cancellation: CancellationSchema },
  { additionalProperties: false }
);

export const ExcelAgentAnswerMessageSchema = Type.Object(
  {
    ...excelBase,
    kind: Type.Literal("answer"),
    answer: Type.Object(
      { outcome: Type.Union([Type.Literal("answered"), Type.Literal("abstained")]) },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);

export const ExcelAgentAuthorizationRequiredMessageSchema = Type.Object(
  {
    ...excelBase,
    kind: Type.Literal("authorization_required"),
    authorization: Type.Union([
      Type.Object(
        {
          requiredAction: Type.Literal("agent.files"),
          reason: Type.Union([
            Type.Literal("product_permission"),
            Type.Literal("resource_permission")
          ]),
          resolution: Type.Literal("contact_administrator")
        },
        { additionalProperties: false }
      ),
      Type.Object(
        {
          requiredAction: Type.Literal("agent.files"),
          reason: Type.Literal("room_membership"),
          resolution: Type.Literal("recheck")
        },
        { additionalProperties: false }
      )
    ])
  },
  { additionalProperties: false }
);

export const ExcelAgentFailureMessageSchema = Type.Object(
  { ...excelBase, kind: Type.Literal("failure"), failure: FailureSchema },
  { additionalProperties: false }
);

export const ExcelAgentCancelledMessageSchema = Type.Object(
  { ...excelBase, kind: Type.Literal("cancelled"), cancellation: CancellationSchema },
  { additionalProperties: false }
);

export const BrowserAgentAnswerMessageSchema = Type.Object(
  {
    ...browserBase,
    kind: Type.Literal("answer"),
    answer: Type.Object(
      { outcome: Type.Union([Type.Literal("answered"), Type.Literal("abstained")]) },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);

export const BrowserAgentFailureMessageSchema = Type.Object(
  { ...browserBase, kind: Type.Literal("failure"), failure: FailureSchema },
  { additionalProperties: false }
);

export const BrowserAgentCancelledMessageSchema = Type.Object(
  { ...browserBase, kind: Type.Literal("cancelled"), cancellation: CancellationSchema },
  { additionalProperties: false }
);

export const AgentMessageSchema = Type.Union([
  AgentAnswerMessageSchema,
  AgentAuthorizationRequiredMessageSchema,
  AgentFailureMessageSchema,
  AgentCancelledMessageSchema,
  AgentApprovalMessageSchema,
  PptAgentAnswerMessageSchema,
  PptAgentAuthorizationRequiredMessageSchema,
  PptAgentFailureMessageSchema,
  PptAgentCancelledMessageSchema,
  ExcelAgentAnswerMessageSchema,
  ExcelAgentAuthorizationRequiredMessageSchema,
  ExcelAgentFailureMessageSchema,
  ExcelAgentCancelledMessageSchema,
  BrowserAgentAnswerMessageSchema,
  BrowserAgentFailureMessageSchema,
  BrowserAgentCancelledMessageSchema
]);

export const AgentApprovalResponseSchema = Type.Object(
  {
    version: Type.Literal("1.0"),
    approvalId: UuidSchema,
    approvalEventId: MatrixEventIdSchema,
    decision: Type.Union([Type.Literal("approved"), Type.Literal("rejected")])
  },
  { additionalProperties: false }
);

export type AgentMessageCitationV1 = Static<typeof AgentMessageCitationSchema>;
export type AgentInvocationTokenUsageV1 = Static<typeof AgentInvocationTokenUsageSchema>;
export type AgentInvocationMetricsV1 = Static<typeof AgentInvocationMetricsSchema>;
export type AgentMessageV1 = Static<typeof AgentMessageSchema>;
export type AgentApprovalResponseV1 = Static<typeof AgentApprovalResponseSchema>;
